import dayjs from 'dayjs';
import { AppContext } from '../../store/context';
const TimesList = AppContext.TimesList;

export default class ValidPatterns {
    private translator: (keyword: string, Object?) => string;

    private rules = {
        required: value => !!value || this.translator('validate_message.Required_field'),
        requiredForNumber: value => {
            if (value) return true;
            const pattern = /^([1-9]\d*|0)$/;
            return pattern.test(value) || this.translator('validate_message.Required_field');
        },
        email: value => {
            if (!value) return true;
            const pattern =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return (
                pattern.test(value) ||
                this.translator('validate_message.Please_enter_in_the_formal_email_address_format')
            );
        },
        url: value => {
            if (!value) return true;
            const pattern = /^[0-9a-zA-Z!#$&'()*+,/:;=?@[\]\-._~]*$/;
            if (pattern.test(value)) {
                return true;
            }
            return this.translator('validate_message.Entered_character_is_invalid');
        },
        acsUrlForOa: value => {
            if (!value) return true;
            const pattern = /^http:\/\/localhost.*\/$/;
            if (pattern.test(value)) {
                return true;
            }
            return this.translator('validate_message.Please_enter_a_URL_starting_with');
        },
        portNumRange: value => {
            if (!value) return true;
            const pattern = /^([1-9]\d*|0)$/;
            return (
                (pattern.test(value) && value <= 65535) ||
                this.translator('validate_message.Please_enter_a_number', { min: 0, max: 65535 })
            );
        },
        onlyNumbers: value => {
            if (!value) return true;
            const pattern = /^([1-9]\d*|0)$/;
            if (pattern.test(value)) {
                return true;
            }
            return this.translator('validate_message.Please_enter_number_only');
        },
        samePassword: (password: string, confirmPassword: string) =>
            password === confirmPassword ? true : this.translator('validate_message.Password_does_not_match'),
        maxChars: (value: string, wordCount: number) => {
            return this.rules.maxChars1Arg(wordCount)(value);
        },
        minChars: (value: string, wordCount: number) => {
            return this.rules.minChars1Arg(wordCount)(value);
        },
        maxChars1Arg: (wordCount: number) => {
            return (value: string) => {
                if (!value) return true;
                return (
                    value.length <= wordCount ||
                    this.translator('validate_message.Please_enter_no_more_than_wordCount_characters', { wordCount })
                );
            };
        },
        minChars1Arg: (wordCount: number) => {
            return (value: string) => {
                if (!value) return true;
                return (
                    value.length >= wordCount ||
                    this.translator('validate_message.Please_enter_no_less_than_wordCount_characters', { wordCount })
                );
            };
        },
        siteLocalUserPattern: value => {
            if (!value) return true;
            const pattern = /^[0-9a-z._-]*$/;
            if (pattern.test(value)) {
                return this.rules.maxChars(value, 256);
            }
            return this.translator(
                'validate_message.Only_half-width_(lower_case_alphabet,_numbers,_dot,_underscore,_hyphen)_can_be_used'
            );
        },
        hostPattern: value => {
            if (!value) return true;
            // \/*?"<>|以外の半角英数記号
            const pattern = /^[!#-)+-.0-;=@-[\]-{}~]*$/;
            if (pattern.test(value)) {
                return this.rules.maxChars(value, 253);
            }
            return this.translator('validate_message.Entered_character_is_invalid');
        },
        fromAddressPattern: value => {
            if (!value) return true;
            const pattern = /^[0-9./]*$/;
            if (!pattern.test(value)) {
                return this.translator('validate_message.Only_half-width_(numeric,_dot,_slashes)_can_be_used');
            }
            return true;
        },
        fqdnPattern: value => {
            if (!value) return true;
            const pattern = /^[0-9a-zA-Z.-]*$/;
            if (pattern.test(value)) {
                return this.rules.maxChars(value, 253);
            }
            return this.translator('validate_message.Only_half-width_(alphanumeric,_dot,_hyphen)_can_be_used');
        },
        domainPattern: value => {
            if (!value) return true;
            const pattern = /^[^:：]*$/;
            if (pattern.test(value)) {
                return this.rules.maxChars(value, 255);
            }
            return this.translator('validate_message.Can_not_use_a_colon');
        },
        netBiosDomainPattern: value => {
            if (!value) return true;
            const pattern = /^[0-9A-Z`~!@#$%^&()_\-+={}[\];',.]*$/;
            if (!pattern.test(value)) {
                return this.translator('validate_message.Entered_character_is_invalid');
            }
            return this.rules.maxChars(value, 15);
        },
        adminPattern: value => {
            if (!value) return true;
            const pattern1 = /^[^\\%&*+/=?{}|<>();:,[\]"￥％＆＊＋＝？｛｝｜＜＞（）；：”\s]*$/;
            const pattern2 = /[^.&@＆＠]$/;
            if (!pattern1.test(value)) {
                return this.translator('validate_message.Entered_character_is_invalid');
            }
            if (!pattern2.test(value)) {
                return this.translator('validate_message.Cannot_use_a_period_(_._)_or_an_at-mark_(_@_)_at_the_end');
            }
            return this.rules.maxChars(value, 64);
        },
        decimalIntegerDigitRange: (value: string, digits: number) => {
            if (!value) return true;
            if (!digits) return true;
            const pattern = /^([1-9]\d*)$/;
            if (!pattern.test(String(digits))) {
                return true;
            }
            let message = this.translator('validate_message.Please_enter_within_the_number_of_consecutive_digits');
            return value.length <= digits || message;
        },
        decimalIntegerRange: (value: string | number, min: number, max: number) => {
            return this.rules.decimalIntegerRange2Arg(min, max)(value);
        },
        decimalIntegerRange2Arg: (min: number, max: number) => {
            // TODO: NSIO-17170
            return (value: string | number) => {
                if (!value) return true;
                const pattern = /^(-?)([1-9]\d*|0)$/;
                const i = parseInt(value as string);
                let message =
                    max == min
                        ? this.translator('validate_message.Please_enter', { word: min })
                        : this.translator('validate_message.Please_enter_a_number', { min: min, max: max });
                return (pattern.test(value as string) && i >= min && i <= max) || message;
            };
        },
        requiredAndDecimalIntegerRange: (min: number, max: number) => {
            return (value: string) => {
                if (!value && parseInt(value) != 0) return this.translator('validate_message.Required_field');
                const pattern = /^(-?)([1-9]\d*|0)$/;
                const i = parseInt(value);
                let message =
                    max == min
                        ? this.translator('validate_message.Please_enter', { word: min })
                        : this.translator('validate_message.Please_enter_a_number', { min: min, max: max });
                return (pattern.test(value) && i >= min && i <= max) || message;
            };
        },
        YYYYMMDDPattern: value => {
            if (!value) {
                return true;
            }
            if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
                return true;
            }
            return this.translator('validate_message.Please_enter_in_YYYY_MM_DD_format');
        },
        requiredYYYYMMDDPattern: value => {
            if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
                return true;
            }
            return this.translator('validate_message.Please_enter_in_YYYY_MM_DD_format');
        },
        HHMMPattern: value => {
            if (!value) {
                return true;
            }
            if (dayjs(value, 'HH:mm', true).isValid()) {
                return this.rules.HHMMRangePattern(value);
            }
            return this.translator('validate_message.Please_enter_in_HH_MM_format');
        },
        HHMMRangePattern: value => {
            if (!value) {
                return true;
            }
            let splitted = value.split(':');
            const hoursList = TimesList.HOURS;
            const minutesList = TimesList.MINUTES;
            if (!hoursList.includes(splitted[0])) {
                return this.translator('validate_message.Name_must_be_a_number_between_min_and_max', {
                    name: this.translator('Hours'),
                    min: '00',
                    max: '23',
                });
            }
            if (!minutesList.includes(splitted[1])) {
                return this.translator('validate_message.Name_must_be_a_number_between_min_and_max', {
                    name: this.translator('Minutes'),
                    min: '00',
                    max: '59',
                });
            }
            return true;
        },
        dateStartEndRelation: (start: Date, end: Date) => {
            if (!start || !end) {
                return true;
            }
            if (start.getTime() < end.getTime()) {
                return true;
            }
            return false;
        },
        dateStartEndRelationString: (start: string, end: string, startLabel: string, endLabel: string) => {
            const startDate = Date.parse(start);
            const endDate = Date.parse(end);
            if (Number.isNaN(startDate) || Number.isNaN(endDate)) {
                return true;
            }
            if (startDate <= endDate) {
                return true;
            }
            return this.translator('validate_message.End_date_must_be_after_start_date', {
                startDate: startLabel,
                endDate: endLabel,
            });
        },
        dateStartEndLimit: (start: Date, end: Date, work_period_limitable: boolean, work_period_limit_days: number) => {
            if (!work_period_limitable || !start || !end) {
                return true;
            }

            // startをそのまま使うと元データも上書きされてしまうのでコピーしたいが、コピーではうまく行かないため、startを使用して新規でDateオブジェクトを作成
            let startDate = new Date(start.getTime());
            startDate.setDate(startDate.getDate() + work_period_limit_days); // 作業期間制限日数を指定

            if (startDate.getTime() >= end.getTime()) {
                return true;
            }
            return false;
        },
        symbolPattern: value => {
            if (!value) return true;
            const pattern = /^(?!.*[^`~!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?/]).*$/;
            if (pattern.test(value)) {
                return this.rules.maxChars(value, 255);
            }
            return this.translator('validate_message.Please_enter_only_symbols', {
                symbols: this.translator('Unusable_Symbols_(_ExcludedSymbols_)'),
            });
        },
        duplication: (value: string, count: number, items: Array<object>, name: string) => {
            if (!value) return true;
            const duplication = items.some((kv, index) => (index !== count ? value === kv['name'] : false));
            if (!duplication) {
                return true;
            }
            return this.translator('validate_message.Duplicate_of_name_exist', {
                name: this.translator(name),
            });
        },
        maxFileSizeKB: (maxSize: number) => {
            return (value: File) => {
                if (!value) return true;
                if (value.size <= 0)
                    return this.translator(
                        'validate_message.Please_select_a_file_whose_file_size_is_greater_than_minSize',
                        { minSize: 0 }
                    );
                return (
                    value.size <= maxSize * 1024 ||
                    this.translator('validate_message.The_maximum_file_size_is_KB', { maxSize })
                );
            };
        },
        certificateFileCheck: (fileContent: string) => {
            if (!fileContent) return true;
            const beginningOfCertificate = '-----BEGIN CERTIFICATE-----';
            if (!fileContent.indexOf(beginningOfCertificate)) return true;
            return this.translator('validate_message.Please_set_certificate');
        },
        isSelectValid: (value: Array<string> | Array<number> | Array<any>, message: string) => {
            if (!value) return true;
            if (value.length > 0) return true;
            return this.translator(message);
        },
        validSelectForNumbers: (value: number, message: string) => {
            if (value > 0 && value != undefined) return true;
            return this.translator(message);
        },
        terminalIpValid: value => {
            if (!value) return true;
            const pattern = /^[0-9.,/ \n]*$/;
            if (!pattern.test(value)) {
                return this.translator(
                    'validate_message.Only_single_byte_characters_(numeric,_dot,_commas,_slashes,_spaces)_and_line_breaks_can_be_used'
                );
            }
            return true;
        },
        unc: value => {
            if (!value) return true;
            const pattern = /[/*?"<>|:`]/;
            if (pattern.test(value)) {
                return this.translator('validate_message.Entered_character_is_invalid');
            }
            return true;
        },
        mountAccountName: value => {
            if (!value) return true;
            const pattern = /[%&*+/=?{}|<>();:,[\]" ]/;
            if (pattern.test(value)) {
                return this.translator('validate_message.Entered_character_is_invalid');
            }
            return true;
        },
        prefix: value => {
            if (!value) return true;
            const pattern = /^[0-9a-zA-Z`~!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?/]*$/;
            if (!pattern.test(value)) {
                return this.translator('validate_message.Entered_character_is_invalid');
            }
            return true;
        },
        alwaysValid: () => {
            return true;
        },
        isCheckValid: (value: Array<string> | Array<number> | Array<any>) => {
            if (!value) return true;
            if (value.length > 0) return true;
            return false;
        },
        duplicationIndex: (values: Array<string> | Array<number> | Array<any>, name: string) => {
            if (new Set(values).size === values.length) return true;
            return this.translator('validate_message.Duplicate_of_name_exist', {
                name: this.translator(name),
            });
        },
        emptyIndex: (values: Array<string> | Array<number> | Array<any>, name: string) => {
            if (values.length === 0) return true;
            if (values.every(value => value.trim() !== '')) return true;
            return this.translator('validate_message.Empty_items_cannot_be_set', {
                name: this.translator(name),
            });
        },
        maxCharsIncludeComma: (value: string, wordCount: number) => {
            if (!value) return true;
            return (
                value.length <= wordCount ||
                this.translator('validate_message.Please_enter_no_more_than_wordCount_characters_including_commas', {
                    wordCount,
                })
            );
        },
        includeItem: (value: string, item: string, isCheckBox: boolean) => {
            if (!value || !item) return true;
            const items = item.split(',');
            let include = false;
            if (isCheckBox) {
                const values = value.split(',');
                include = values.every(value => items.includes(value));
            } else {
                include = items.includes(value);
            }
            if (include) {
                return true;
            }
            return false;
        },
        positiveInteger: value => {
            if (!value) return true;
            const pattern = /^([1-9]\d*)$/;
            if (pattern.test(value)) return true;
            return this.translator('validate_message.Please_enter_a_positive_number');
        },
        // 正規表現構文チェック
        regExp: value => {
            if (!value) return true;
            let regExpFlag = false;
            try {
                new RegExp(value);
            } catch (e) {
                regExpFlag = true;
            }
            return !regExpFlag || this.translator('validate_message.Regular_expression_is_incorrect');
        },
    };

    constructor(translator: (keyword: string, Object?) => string) {
        if (!translator) translator = key => key;
        this.translator = translator;
    }

    public getRules() {
        return this.rules;
    }
}
